import { queryOptions, useQuery } from '@tanstack/react-query';

import { ApiResponse, QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const apiFn = apiClient.dbo.getCachingRules;

type CachingRulesQueryData = NonNullable<ApiResponse<typeof apiFn>['items']>;

type Params = {
  cacheGroupId?: string;
  cacheId?: string;
  templateHash?: string;
  table?: string;
};

type UseCachingRulesQueryArgs<SELECT> = QueryOptions<
  CachingRulesQueryData,
  SELECT
> &
  Params;

export const DBO_CACHE_RULES_QUERY_KEY = 'dbo-cache-rules';

export const getCachingRulesQueryKey = (
  params: {
    cacheGroupId?: string;
    cacheId?: string;
    templateHash?: string;
    table?: string;
  } = {}
) => {
  if (!params) return [DBO_CACHE_RULES_QUERY_KEY] as const;
  return [DBO_CACHE_RULES_QUERY_KEY, params] as const;
};

const getCachingRulesQueryOptions = ({
  cacheGroupId,
  cacheId,
  templateHash,
  table,
  enabled = true,
}: Params & {
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getCachingRulesQueryKey({
      cacheGroupId,
      cacheId,
      templateHash,
      table,
    }),
    queryFn: async () => {
      const { data } = await apiFn({
        groupId: cacheGroupId!,
        cacheId: cacheId!,
        ...(templateHash && { templateHash }),
        ...(table && { table }),
      });
      return data.items || [];
    },
    enabled: makeIsEnabled(enabled, !!cacheGroupId, !!cacheId),
  });

export const useCachingRulesQuery = <SELECT = CachingRulesQueryData>({
  cacheGroupId,
  cacheId,
  templateHash,
  table,
  enabled,
  select,
  refetchInterval,
}: UseCachingRulesQueryArgs<SELECT>) => {
  return useQuery({
    ...getCachingRulesQueryOptions({
      cacheGroupId,
      cacheId,
      templateHash,
      table,
      enabled,
    }),
    select,
    refetchInterval,
  });
};
